<template>
  <div class="reviews-item" @click="handleToReviewDetailClick">
    <div class="header">
      <img class="icon" v-webp src="https://image.kilimall.com/global/images/avatar.png" alt="">
      <div class="user-info">
        <div class="user-name">{{ reviewItem.commentBase.accountName }}</div>
        <div class="review-time">
          {{ reviewItem.replyTime }} | {{ reviewItem.commentBase?.productInfo?.spec }}
        </div>
      </div>
      <van-rate :model-value="Number(reviewItem.commentBase?.productScore)" allow-half :size="isPC ? '18' : '8'" />
    </div>

    <div class="review-content">
      <p class="content-text">{{ reviewItem.commentContent?.content }}</p>
      <div class="media-list">
        <div
          v-for="(item, idx) in mediaList"
          :key="idx"
          :class="useIsMobile().value ? 'media-item' : 'media-item pc__media-item'"
        >
          <div class="inner-item" @click.stop="handlePreviewVideoClick(idx)">
            <img class="preview-box" v-lazy="$cvWebpFn(item.mediaContents, { w: 124 })" alt="" v-if="item.mediaType === 1">
            <div v-if="item.mediaType === 2">
              <img v-lazy="$cvWebpFn(item.mediaContents)" alt="" class="preview-box">
              <span class="play-icon"><van-icon size="32" name="play-circle-o" /></span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="option-bar" v-if="showOptionsBar">
      <span class="chat-o-span" v-if="!isPC">
        <van-icon name="chat-o" />
        {{ reviewItem.commentBase.repliesCount }}
      </span>
      <span v-else style="font-size: 18px;" @click.stop="showDownloadPopup = true">
        <van-icon :size="22" name="chat-o" />
        {{ reviewItem.commentBase.repliesCount }}
      </span>
      <span :style="{ fontSize: isPC ? '18px':'' }" @click.stop="handleLikesClick">
        <van-icon :size="isPC ? '22' : ''" name="good-job-o" v-if="!reviewItem.commentContent.isLiked" />
        <van-icon :size="isPC ? '22' : ''" name="good-job" color="#DD3131" v-else />
        {{ reviewItem.commentBase.likesCount }}
      </span>
      <p class="favour-tips" v-if="reviewItem.commentBase.likesCount > 0">
        {{ reviewItem.commentBase.likesCount }} people found this helpful
      </p>
    </div>

    <template v-if="!hiddenReply && reviewItem.sellerReply">
      <div  class="seller-reply">
        <p class="store-name">
          <van-icon name="shop-o" size="18" /> Seller reply:
        </p>
        <p class="reply-text">{{ reviewItem.sellerReply.content }}</p>
      </div>
    </template>
  </div>

  <PreviewVideo
    v-model:show="previewMediaShow"
    v-model:curIdx="currentPreviewIndex"
    :media-list="mediaList"
  />

  <LazyPCAppDownloadPopup
    v-model:show="showDownloadPopup"
    title="See What Everyone Is Saying on Kilimall APP!"
    desc="Want the full story? 
          Access all comments, replies, 
          and unboxing photos in the App. 
          Download now to shop 
          with confidence & more discount！"
    left-image="https://image.kilimall.com/lite/new_wap/download-app/pc-download-app-review-left.png"
    right-image="https://image.kilimall.com/lite/new_wap/download-app/pc-download-app-review-right.png"
  />
</template>

<script setup lang="ts">
import { showToast } from 'vant';
import PreviewVideo from './PreviewVideo.vue'
import { submitFavour } from '@/api/models/reviews'
import {
  getTrafficTrackFromRoute,
  trafficTrackToRouteQuery,
  type ITrafficTrackFields
} from '@/plugins/utils'

const props = withDefaults(
  defineProps<{ 
    reviewItem: any,
    showOptionsBar?: boolean
    isPC?: boolean
    hiddenReply?: boolean
    trafficTrack?: ITrafficTrackFields
  }>(),
  {
    showOptionsBar: true,
    isPC: false
  }
)

const mediaList = computed(() => {
  if(!props.reviewItem.commentContent) {
    return []
  }

  const { images, videos } = props.reviewItem.commentContent
  const imagesList = images.map((imgItem: string) => ({mediaType: 1, mediaContents: imgItem}))
  let videoList:any = []
  videos.forEach((videoItem: any) => {
    if (!videoItem.url) return
    videoList.push({mediaType: 2, mediaContents: videoItem.cover, dashMediaContents: videoItem.url})
  })
  
  return [ ...imagesList, ...videoList ]
})
const emits = defineEmits<{
  (evnet: 'update:likes'): void
}>()
const router = useRouter()
const route = useRoute()

const previewMediaShow = ref<boolean>(false)
const currentPreviewIndex = ref<number>(0)
const showDownloadPopup = ref<boolean>(false)
const handlePreviewVideoClick = (idx: number) => {
  currentPreviewIndex.value = idx
  previewMediaShow.value = true
}
const handleToReviewDetailClick = () => {
  if(!props.isPC) {
    const track = props.trafficTrack?.source
      ? props.trafficTrack
      : getTrafficTrackFromRoute(route.query as Record<string, unknown>)

    router.push({
      path: `/reviews/detail_${props.reviewItem.commentBase.id}`,
      query: trafficTrackToRouteQuery(track)
    })
  }
}

const handleLikesClick = async() => {
  const commentContentsId = props.reviewItem.commentContent.id
  await submitFavour(commentContentsId).then(() => {
    emits('update:likes')
  }).catch((e: any) => {
    if(e.code === 1001) {
        return
      }
    showToast(e.msg)
  })
}
</script>

<style lang="scss" scoped>
.reviews-item{
  margin: 0 20px;
  padding: 20px 0;
}
.header{
  display: flex;
  align-items: center;
  .icon{
    width: 80px;
    height: 80px;
    min-width: 80px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid #efefef;
  }
  .user-info{
    flex: 1;
    padding: 0 10px;
    .user-name {
      font-size: 28px;
    }
    .review-time{
      color: #999;
      font-size: 24px;
      margin-top: 10px;
    }
  }
}
.review-content{
  padding-left: 80px;
}
.content-text{
  padding-left: 10px;
}
.media-list{
  display: flex;
  flex-wrap: wrap;
  .media-item{
    width: 25%;
    padding: 5px;
    .inner-item{
      width: 100%;
      position: relative;
      height: 0;
      padding-bottom: 100%;
      .play-icon{
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        color: #fff;
        background-color: rgba($color: #000000, $alpha: .3);
        @include center();
      }
      .preview-box{
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      img{
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
      .preview-video{
        width: 100%;
        height: 100%;
      }
    }
  }
  .pc__media-item{
    width: 10%;
  }
}
.seller-reply{
  background-color: #efefef;
  border-radius: 8px;
  padding: 15px;
  margin-top: 10px;
  margin-left: 90px;
  .store-name{
    color: #333;
    font-size: 26px;
  }
  .reply-text{
    margin-top: 6px;
    color: #666;
    font-size: 24px;
  }
}
.option-bar{
  text-align: right;
  padding-top: 10px;
  span{
    color: #999;
    font-size: 28px;
    cursor: pointer;
  }
  .chat-o-span {
    padding-right: 15px;
  }
  .favour-tips{
    color: #999;
    font-size: 24px;
  }
}
</style>